/* eslint-disable fp/no-mutation */
import React, { ReactElement, useState } from 'react'
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Paper,
  Alert,
  Chip,
} from '@mui/material'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch } from 'src/store/hooks'
import {
  createReferences,
  updateReferences,
  fetchReferences,
  fetchEventCompliances,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import { ContentComplianceStatus } from 'src/v3/core/types'

type Reference = {
  id?: string
  text: string
}

type ReferencesManagementProps = {
  eventID: string
  references?: {
    id: string
    text: string
    linkedServiceID: string | null
    createdTime: string
    updatedTime: string
  }[]
  referencesStatus?: string
  complianceStatus?: ContentComplianceStatus
}

export default ({
  eventID,
  references = [],
  complianceStatus,
}: ReferencesManagementProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [isEditing, setIsEditing] = useState(false)
  let [loading, setLoading] = useState(false)

  let hasExistingReferences = references.length > 0

  let form = useForm({
    defaultValues: {
      references: hasExistingReferences
        ? references.map((ref) => ({ text: ref.text }))
        : [{ text: '' }],
    },
    onSubmit: async ({ value }) => {
      setLoading(true)
      try {
        // Filter out empty references
        let validReferences = value.references.filter(
          (ref) => ref.text.trim().length > 0,
        )

        if (validReferences.length === 0) {
          setLoading(false)
          return
        }

        if (hasExistingReferences) {
          // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
          await dispatch(
            updateReferences({
              eventID,
              references: validReferences,
            }),
          )
        } else {
          // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
          await dispatch(
            createReferences({
              eventID,
              references: validReferences,
            }),
          )
        }

        // Refetch references and compliance statuses
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchReferences(eventID))
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchEventCompliances())
        setIsEditing(false)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to save references:', error)
      } finally {
        setLoading(false)
      }
    },
  })

  let addReference = () => {
    let currentRefs = form.getFieldValue('references') || []
    form.setFieldValue('references', [...currentRefs, { text: '' }])
  }

  let removeReference = (index: number) => {
    let currentRefs = form.getFieldValue('references') || []
    if (currentRefs.length > 1) {
      form.setFieldValue(
        'references',
        currentRefs.filter((_, i) => i !== index),
      )
    }
  }

  let startEditing = () => {
    // Reset form with current references
    form.setFieldValue(
      'references',
      hasExistingReferences
        ? references.map((ref) => ({ text: ref.text }))
        : [{ text: '' }],
    )
    setIsEditing(true)
  }

  let cancelEditing = () => {
    setIsEditing(false)
    form.reset()
  }

  if (!isEditing && !hasExistingReferences) {
    return (
      <Box
        sx={{
          p: 3,
          bgcolor: 'background.paperDark',
          borderRadius: 1,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          No References Added
        </Typography>
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{
            mb: 2,
          }}
        >
          Add scholarly references and citations to support your course content.
        </Typography>
        <Button
          variant="contained"
          onClick={startEditing}
          aria-label="Add references"
        >
          Add References
        </Button>
      </Box>
    )
  }

  if (!isEditing && hasExistingReferences) {
    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" component="h4">
            References ({references.length})
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={startEditing}
            aria-label="Edit references"
          >
            Edit References
          </Button>
        </Box>
        {/* References Compliance Status */}
        {complianceStatus && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              p: 2,
              bgcolor: 'background.paperDark',
              borderRadius: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 'medium',
              }}
            >
              References Status
            </Typography>
            <Chip
              label={
                complianceStatus === 'approved'
                  ? 'Approved'
                  : complianceStatus === 'rejected'
                    ? 'Rejected'
                    : complianceStatus === 'updated'
                      ? 'Updated - Pending Review'
                      : complianceStatus === 'unapproved'
                        ? 'Unapproved'
                        : 'Pending Review'
              }
              variant="outlined"
              sx={{
                color:
                  complianceStatus === 'approved'
                    ? 'success'
                    : complianceStatus === 'rejected' ||
                        complianceStatus === 'unapproved'
                      ? 'error'
                      : 'primary',
              }}
            />
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {references.map((reference, index) => (
            <Paper key={reference.id} variant="outlined" sx={{ p: 2 }}>
              <Typography
                color="text.secondary"
                variant="body2"
                sx={{
                  mb: 0.5,
                }}
              >
                Reference {index + 1}
              </Typography>
              <Typography variant="body1">{reference.text}</Typography>
            </Paper>
          ))}
        </Box>
      </Box>
    )
  }

  // Editing mode
  return (
    <Box>
      <Typography variant="h6" component="h4" sx={{ mb: 2 }}>
        {hasExistingReferences ? 'Edit References' : 'Add References'}
      </Typography>

      <form.Field
        name="references"
        children={(field) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {(field.state.value as Reference[]).map((reference, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label={`Reference ${index + 1}`}
                  value={reference.text}
                  onChange={(e) => {
                    let updatedRefs = [...(field.state.value as Reference[])]
                    updatedRefs[index] = {
                      ...updatedRefs[index],
                      text: e.target.value,
                    }
                    field.handleChange(updatedRefs)
                  }}
                  sx={{ bgcolor: 'background.paperDark' }}
                  placeholder="Enter citation in proper academic format (e.g., Smith, J. (2023). Title. Journal, 45(2), 123-135.)"
                  disabled={loading}
                />
                <IconButton
                  onClick={() => removeReference(index)}
                  disabled={
                    (field.state.value as Reference[]).length <= 1 || loading
                  }
                  aria-label={`Remove reference ${index + 1}`}
                  sx={{ mt: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      />

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 3 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={addReference}
          disabled={loading}
          aria-label="Add another reference"
        >
          Add Reference
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Use proper academic citation format. Examples: APA, MLA, or Chicago
        style depending on your field.
      </Alert>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          onClick={() => form.handleSubmit()}
          disabled={loading}
          aria-label="Save references"
        >
          {loading
            ? 'Saving...'
            : hasExistingReferences
              ? 'Update References'
              : 'Save References'}
        </Button>
        <Button
          variant="outlined"
          onClick={cancelEditing}
          disabled={loading}
          aria-label="Cancel editing references"
        >
          Cancel
        </Button>
      </Box>
    </Box>
  )
}
